// src/components/Procurement/SupplierPaymentPanel.jsx
// SF-112: shared inline payment confirmation panel for GIT tab and Payments tab
import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import Combobox from '../Combobox';
import FifoSettlementPreview from './FifoSettlementPreview';
import { isPickableFunding } from '../../lib/resolveSupplierAccount';   // 565: shared account-picker rule
import AllocationEditor from './AllocationEditor';
import { AmountInput, TextInput } from '../inputs/Input';

export default function SupplierPaymentPanel({
  open,
  onClose,
  onSuccess,
  partnerId,
  partnerName,
  operationId,
  financeRowId,
  prefilledAmount,
  accountToId,
  landedCostId,   // Prompt 197 Part B: when paying a specific LC, tag the payment to it
  title,          // optional panel heading override
  capAmount = null,
  reconciliation = null,
}) {
  const todayStr = new Date().toISOString().slice(0, 10);

  const [paymentDate,      setPaymentDate]      = useState(todayStr);
  const [amount,           setAmount]           = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [notes,            setNotes]            = useState('');
  const [accountOptions,   setAccountOptions]   = useState([]);
  const [submitting,       setSubmitting]       = useState(false);
  const [error,            setError]            = useState(null);
  // Two-phase flow (Item 3a): 'form' collects the payment; on a successful goods/supplier POST we
  // switch to 'allocate' and mount the AllocationEditor on the freshly-posted leg so the user can
  // apply it to specific orders (goods + LC). onSuccess() fires on Done — NOT right after the POST —
  // so the parent doesn't close before the split is chosen. Tagged per-LC payments and cash orders
  // (no payable to allocate) skip straight to onSuccess (as before).
  const [phase,            setPhase]            = useState('form');
  const [postedLegId,      setPostedLegId]      = useState(null);
  // POST /finance has no client idempotency key. This ref closes the double-click window before
  // React has rendered the disabled button, so one confirmation can create only one money leg.
  const submitInFlight = useRef(false);
  const cap = Number(capAmount);
  const hasCap = Number.isFinite(cap) && cap > 0;
  const overCap = hasCap && Number(amount) > cap + 0.005;

  // Fetch Cash + Bank accounts when the panel opens
  useEffect(() => {
    if (!open) return;
    api.getAccounts()
      .then(data => {
        const options = (data || [])
          .filter(a => isPickableFunding(a, ['cash', 'bank']))
          .map(a => ({ value: a.id, label: a.name }));
        setAccountOptions(options);
      })
      .catch(() => {});
  }, [open]);

  // Sync prefilled amount whenever it changes
  useEffect(() => {
    setAmount(prefilledAmount != null ? String(prefilledAmount) : '');
  }, [prefilledAmount, open]);

  // Reset form fields when panel is reopened
  useEffect(() => {
    if (open) {
      setPaymentDate(todayStr);
      setSelectedAccountId('');
      setNotes('');
      setError(null);
      setPhase('form');
      setPostedLegId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  async function handleSubmit() {
    if (submitInFlight.current) return;
    if (!selectedAccountId) {
      setError('Please select a payment account');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const leg = await api.request('/finance', {
        method: 'POST',
        body: JSON.stringify({
          type: 'Payment',
          subtype: 'Supplier Payment',
          partner_id: partnerId || null,
          amount: Number(amount),
          account_from_id: selectedAccountId,
          account_to_id: accountToId || null,
          date: paymentDate,
          notes: notes || null,
          operation_id: operationId || null,
          landed_cost_id: landedCostId || null,   // Prompt 197 Part B: per-LC payment tag
          tx_status: 'posted',
        }),
      });
      if (financeRowId) {
        await api.request(`/finance/${financeRowId}`, {
          method: 'PUT',
          body: JSON.stringify({ status: 'Paid' }),
        });
      }
      // A tagged per-LC payment is not FIFO/cash-application settled — nothing to allocate; close as
      // before. Otherwise open the allocation editor on the posted leg (the POST already auto-FIFO-
      // allocated; the editor lets the user re-split). Guard: if the leg has no id or the allocations
      // endpoint rejects (cash order / no payable), skip the editor and finish.
      const legId = leg?.id || null;
      if (landedCostId || !legId) { onSuccess(); return; }
      let hasPayable = true;
      try { await api.request(`/payment-allocations?settlement_leg_id=${legId}`); }
      catch { hasPayable = false; }
      if (!hasPayable) { onSuccess(); return; }
      setPostedLegId(legId);
      setPhase('allocate');
    } catch (e) {
      setError(e.message || 'Payment failed. Please try again.');
    } finally {
      submitInFlight.current = false;
      setSubmitting(false);
    }
  }

  if (phase === 'allocate') {
    return (
      <div style={{ background: '#f4f8f5', borderBottom: '1px solid var(--line)', padding: '14px 20px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>
          Payment recorded — apply it to orders
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
          The backend applied FIFO first. Adjust it below, or choose “Leave on account” then save to carry the remainder as a supplier credit.
        </div>
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>Allocate / adjust FIFO split (optional)</summary>
          <div style={{ marginTop: 10 }}>
            <AllocationEditor settlementLegId={postedLegId} operationId={operationId || null} />
          </div>
        </details>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
          Need the full payment worklist? <a href="/finance/payables/allocate">Open Allocations</a>.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn success sm"
            data-testid="pay-done"
            onClick={() => onSuccess()}
            >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f4f8f5', borderBottom: '1px solid var(--line)', padding: '14px 20px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>
        {title || `Record Payment — ${partnerName || '—'}`}
      </div>
      {reconciliation}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12, maxWidth: 640 }}>
        {/* Payment date */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
            Payment date
          </label>
          <TextInput
            type="date"
            value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)}
            style={{ border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', fontSize: 12, width: '100%' }}
          />
        </div>
        {/* Amount */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
            Amount{hasCap ? ` · dues ${cap.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} F` : ''}
          </label>
          <AmountInput
            data-testid="pay-amount"
            min="0"
            step="any"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            style={{ border: `1px solid ${overCap ? '#b14a30' : 'var(--line)'}`, borderRadius: 4, padding: '5px 8px', fontSize: 12, width: '100%' }}
          />
          {hasCap && <button type="button" onClick={() => setAmount(String(cap))} style={{ marginTop: 4, fontSize: 10.5, background: 'none', border: '1px solid var(--ctl-border)', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', color: 'var(--muted)' }}>Fill payable ({cap.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} F)</button>}
        </div>
        {/* Account from */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
            Account from
          </label>
          <div data-testid="pay-account">
            <Combobox
              options={accountOptions}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
              placeholder="Select payment account"
            />
          </div>
        </div>
        {/* Notes */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Optional notes"
            style={{ border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', fontSize: 12, width: '100%', resize: 'vertical' }}
          />
        </div>
      </div>
      {/* Prompt 197 Part B — shared FIFO settlement preview (goods/supplier payment only; a tagged
          per-LC payment is not FIFO-settled). */}
      {!landedCostId && (
        <div data-testid="pay-fifo-preview"><FifoSettlementPreview partnerId={partnerId} accountId={accountToId} amount={amount} operationId={operationId} /></div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{error}</div>
      )}
      {overCap && <div style={{ fontSize: 11, color: '#8a5a08', marginBottom: 8 }}>{(Number(amount) - cap).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} F above current dues → recorded as on-account credit (applied oldest-first, the remainder held for this supplier).</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn success sm"
          data-testid="pay-submit"
          onClick={handleSubmit}
          disabled={submitting}
          style={{ opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Processing…' : 'Confirm Payment'}
        </button>
        <button
          onClick={onClose}
          style={{
            fontSize: 12, background: 'none', border: '1px solid var(--ctl-border)',
            borderRadius: 5, padding: '6px 12px', cursor: 'pointer',
          }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
